import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/** Briefly keep a desktop submenu open while the pointer crosses into it. */
const HOVER_CLOSE_DELAY_MS = 180;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverCloseTimer;

  /** @type {HTMLElement[]} */
  #desktopHoverItems = [];

  /**
   * Prevent duplicate custom-menu work when Horizon morphs several header
   * descendants in the same hydration pass.
   */
  #customMenuHydrationQueued = false;

  /** @type {MutationObserver | null} */
  #customMenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#hydrateCustomMegaMenus();
    this.#preloadImages();
    this.#observeCustomMegaMenus();
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#bindDesktopHoverFallback();
  }

  /**
   * Horizon hydrates the header after the initial page render. Re-apply custom
   * image menus after that morph so the server-rendered native menu cannot
   * overwrite them.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#queueCustomMegaMenuHydration();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#unbindDesktopHoverFallback();
    this.#cleanupMutationObserver();
    this.#customMenuMutationObserver?.disconnect();
    this.#customMenuMutationObserver = null;
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = undefined;
    this.#customMenuHydrationQueued = false;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #queueCustomMegaMenuHydration() {
    if (this.#customMenuHydrationQueued) return;

    this.#customMenuHydrationQueued = true;
    queueMicrotask(() => {
      this.#customMenuHydrationQueued = false;
      if (!this.isConnected) return;

      this.#hydrateCustomMegaMenus();
      this.#preloadImages();
      this.#unbindDesktopHoverFallback();
      this.#bindDesktopHoverFallback();
    });
  }

  /**
   * Watch for Horizon's delayed header morph. The morph can restore native
   * submenu markup without reconnecting this custom element.
   */
  #observeCustomMegaMenus() {
    this.#customMenuMutationObserver?.disconnect();
    this.#customMenuMutationObserver = new MutationObserver(() => {
      if (this.#needsCustomMegaMenuHydration()) {
        this.#queueCustomMegaMenuHydration();
      }
    });
    this.#customMenuMutationObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-dd-custom-mega-menu'],
    });
  }

  /**
   * @returns {boolean}
   */
  #needsCustomMegaMenuHydration() {
    const templates = Array.from(this.querySelectorAll('template[data-dd-mega-menu]'));
    const configuredTriggers = new Set(
      templates
        .filter(
          (template) =>
            template instanceof HTMLTemplateElement &&
            templateHasCustomMenuContent(template) &&
            normalizeMenuLabel(template.dataset.menuTrigger)
        )
        .map((template) => normalizeMenuLabel(/** @type {HTMLTemplateElement} */ (template).dataset.menuTrigger))
    );

    if (configuredTriggers.size === 0) return false;

    const customTriggers = new Set(
      Array.from(this.querySelectorAll('.menu-list__submenu[data-dd-custom-mega-menu]')).map((submenu) => {
        const title = submenu.parentElement?.querySelector(':scope > [ref="menuitem"] .menu-list__link-title');
        return normalizeMenuLabel(title?.textContent);
      })
    );

    return Array.from(configuredTriggers).some((trigger) => {
      const menuExists = Array.from(
        this.querySelectorAll('.menu-list__list-item:not([slot="more"]) .menu-list__link-title')
      ).some((title) => normalizeMenuLabel(title.textContent) === trigger);
      return menuExists && !customTriggers.has(trigger);
    });
  }

  /**
   * Replace a matching native submenu with merchant-configured image cards.
   * Empty placeholder submenus are removed so ordinary top-level links keep
   * their normal navigation behavior.
   */
  #hydrateCustomMegaMenus() {
    const templates = Array.from(this.querySelectorAll('template[data-dd-mega-menu]'));
    const menuItems = Array.from(this.querySelectorAll('.menu-list__list-item:not([slot="more"])'));
    const configuredTriggers = new Set();

    templates.forEach((template) => {
      if (!(template instanceof HTMLTemplateElement)) return;

      const triggerName = normalizeMenuLabel(template.dataset.menuTrigger);
      const hasMenuContent = templateHasCustomMenuContent(template);
      if (!triggerName || !hasMenuContent || configuredTriggers.has(triggerName)) return;

      const menuItem = menuItems.find((item) => {
        const title = item.querySelector(':scope > [ref="menuitem"] .menu-list__link-title');
        return normalizeMenuLabel(title?.textContent) === triggerName;
      });
      if (!(menuItem instanceof HTMLElement)) return;

      const triggerLink = menuItem.querySelector(':scope > [ref="menuitem"]');
      const submenu = menuItem.querySelector(':scope > .menu-list__submenu');
      const submenuInner = submenu?.querySelector(':scope > .menu-list__submenu-inner');
      if (!(triggerLink instanceof HTMLElement) || !(submenu instanceof HTMLElement) || !submenuInner) return;

      submenuInner.replaceChildren(template.content.cloneNode(true));
      this.#initializeProductMegaMenus(submenuInner);
      submenu.removeAttribute('data-dd-mega-menu-placeholder');
      submenu.dataset.ddCustomMegaMenu = '';

      if (!submenuInner.id) {
        submenuInner.id = `dd-mega-menu-${configuredTriggers.size + 1}`;
      }
      triggerLink.setAttribute('aria-controls', submenuInner.id);
      triggerLink.setAttribute('aria-haspopup', 'true');
      triggerLink.setAttribute('aria-expanded', 'false');
      this.#ensureMenuCaret(triggerLink);
      configuredTriggers.add(triggerName);
    });

    this.querySelectorAll('[data-dd-mega-menu-placeholder]').forEach((placeholder) => placeholder.remove());
  }

  /**
   * Build category navigation and product panels for dynamic product menus.
   * @param {Element} root
   */
  #initializeProductMegaMenus(root) {
    root.querySelectorAll('[data-dd-product-mega-menu]').forEach((menu, menuIndex) => {
      if (!(menu instanceof HTMLElement) || menu.dataset.ddProductMenuInitialized === 'true') return;

      const navigation = menu.querySelector('[data-dd-product-category-navigation]');
      const panels = menu.querySelector('[data-dd-product-panels]');
      const sourceContainer = menu.querySelector('[data-dd-product-category-sources]');
      if (!(navigation instanceof HTMLElement) || !(panels instanceof HTMLElement) || !sourceContainer) return;

      const triggers = [];
      const productPanels = [];

      sourceContainer.querySelectorAll('[data-dd-product-category-source]').forEach((source, sourceIndex) => {
        const trigger = source.querySelector('[data-dd-product-category-trigger]');
        const panel = source.querySelector('[data-dd-product-category-panel]');
        if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

        const itemIndex = triggers.length;
        const triggerId = trigger.id || `dd-product-trigger-${menuIndex + 1}-${sourceIndex + 1}`;
        const panelId = panel.id || `dd-product-panel-${menuIndex + 1}-${sourceIndex + 1}`;
        trigger.id = triggerId;
        panel.id = panelId;
        trigger.setAttribute('aria-controls', panelId);
        trigger.setAttribute('aria-selected', 'false');
        panel.setAttribute('aria-labelledby', triggerId);
        panel.hidden = true;

        const activate = () => this.#activateProductMegaMenuCategory(triggers, productPanels, itemIndex);
        trigger.addEventListener('pointerenter', activate);
        trigger.addEventListener('focus', activate);

        triggers.push(trigger);
        productPanels.push(panel);
        navigation.append(trigger);
        panels.append(panel);
        source.remove();
      });

      sourceContainer.remove();
      if (triggers.length === 0) return;

      menu.dataset.ddProductMenuInitialized = 'true';
      this.#activateProductMegaMenuCategory(triggers, productPanels, 0);
    });
  }

  /**
   * @param {HTMLElement[]} triggers
   * @param {HTMLElement[]} panels
   * @param {number} activeIndex
   */
  #activateProductMegaMenuCategory(triggers, panels, activeIndex) {
    triggers.forEach((trigger, index) => {
      const isActive = index === activeIndex;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', isActive.toString());
    });

    panels.forEach((panel, index) => {
      const isActive = index === activeIndex;
      panel.hidden = !isActive;
      if (!isActive) return;

      panel.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
    });
  }

  /**
   * Add a submenu direction indicator to a dynamically configured menu.
   * @param {HTMLElement} triggerLink
   */
  #ensureMenuCaret(triggerLink) {
    if (this.dataset.showMenuCaret === 'false' || triggerLink.querySelector('.dd-menu-caret')) return;

    const caret = document.createElement('span');
    caret.className = 'dd-menu-caret';
    caret.setAttribute('aria-hidden', 'true');
    triggerLink.append(caret);
  }

  /**
   * Explicit mouse listeners keep hover opening reliable in theme-editor previews
   * and browsers where delegated pointerenter events report a nested target.
   */
  #bindDesktopHoverFallback() {
    this.#desktopHoverItems = Array.from(this.querySelectorAll('.menu-list__list-item')).filter(
      (item) => item instanceof HTMLElement
    );

    this.#desktopHoverItems.forEach((item) => {
      item.addEventListener('mouseenter', this.#desktopHoverEnter);
      item.addEventListener('mouseleave', this.#desktopHoverLeave);
    });
  }

  #unbindDesktopHoverFallback() {
    this.#desktopHoverItems.forEach((item) => {
      item.removeEventListener('mouseenter', this.#desktopHoverEnter);
      item.removeEventListener('mouseleave', this.#desktopHoverLeave);
    });
    this.#desktopHoverItems = [];
  }

  #desktopHoverEnter = (event) => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    this.activate(event);
  };

  #desktopHoverLeave = (event) => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    this.deactivate(event);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = undefined;

    const isMoreTrigger = event.target.slot === 'more';
    const item = findMenuItem(event.target);
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      previouslyActiveOverflowItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      overflowItem.ariaExpanded = 'true';
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    if (
      (event.type === 'pointerleave' || event.type === 'mouseleave') &&
      window.matchMedia('(hover: hover)').matches
    ) {
      const activeItem = this.#state.activeItem;
      clearTimeout(this.#hoverCloseTimer);
      this.#hoverCloseTimer = setTimeout(() => {
        this.#hoverCloseTimer = undefined;
        if (activeItem?.parentElement?.matches(':hover')) return;
        this.#deactivate(activeItem);
      }, HOVER_CLOSE_DELAY_MS);
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      activeOverflowItem.ariaExpanded = 'false';
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll(
      'img[loading="lazy"]:not(.dd-product-mega-menu__product-image)'
    );
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return element instanceof HTMLElement ? element : null;
  }

  const nestedItem = element.querySelector('[ref="menuitem"]');
  if (nestedItem instanceof HTMLElement) return nestedItem;

  const parentItem = element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
  return parentItem instanceof HTMLElement ? parentItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Normalize merchant-entered and storefront menu labels for matching.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeMenuLabel(value) {
  return value?.trim().replace(/\s+/g, ' ').toLocaleLowerCase() ?? '';
}

/**
 * @param {HTMLTemplateElement} template
 * @returns {boolean}
 */
function templateHasCustomMenuContent(template) {
  return Boolean(
    template.content.querySelector(
      '.dd-mega-menu-card, .dd-product-mega-menu__category-source, .dd-column-mega-menu__group'
    )
  );
}
